import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, PackageOpen } from "lucide-react";

/**
 * Product Suggestion Dialog — Prompt 32 refresh.
 *
 * Shown when a barcode scan matches an external provider. Optimized for
 * kitchen speed: no product image (the item is already in the chef's
 * hand). Read-only identification card by default; the chef either
 * confirms it as-is with **Use Product**, or expands to **Edit Before
 * Creating**, or **Cancel**s.
 *
 * Prices are NEVER imported here — they always come from Deliveries.
 */
export default function ProductSuggestionDialog({
    suggestion, restaurantId, onClose, onCreated,
}) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [f, setF] = useState({
        barcode:      suggestion.barcode || "",
        product_name: suggestion.product_name || "",
        brand:        suggestion.brand || "",
        package_size: suggestion.package_size || "",
        unit:         suggestion.unit || "kpl",
        category:     suggestion.category || "General",
    });
    const [busy, setBusy] = useState(false);
    const upd = (patch) => setF((x) => ({ ...x, ...patch }));
    const canSubmit = f.product_name.trim().length > 1 && f.unit.trim().length > 0;

    const submit = async () => {
        setBusy(true);
        try {
            // Compose a display name from brand + product + size when the
            // chef hasn't already merged them.
            const nameParts = [f.product_name];
            if (f.brand && !f.product_name.toLowerCase().includes(f.brand.toLowerCase())) {
                nameParts.unshift(f.brand);
            }
            if (f.package_size && !f.product_name.includes(f.package_size)) {
                nameParts.push(`(${f.package_size})`);
            }
            const composedName = nameParts.join(" ").replace(/\s+/g, " ").trim();
            const payload = {
                product_name: composedName,
                barcode: f.barcode,
                unit: f.unit,
                category: f.category || "General",
                area: "Other",
                // NOTE: no price fields — see backend barcode_providers.
                restaurant_id: restaurantId || undefined,
            };
            const { data } = await api.post("/products", payload);
            toast.success(t("productSuggestion.productCreatedFromBarcodeSuggestion", "Product created from barcode suggestion"));
            onCreated?.({ id: data.id, ...payload });
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    const providerLabel = {
        gs1_finland:      "GS1 Finland / Synkka",
        open_food_facts:  "Open Food Facts",
        internal:         t("productSuggestion.localDatabase", "Local database"),
    }[suggestion.source] || suggestion.source;

    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent
                className="max-w-md"
                data-testid="product-suggestion-dialog"
                // Prompt 34 — do NOT dismiss a found product on stray taps.
                // Only Cancel, Close X, Use Product or Create Product can close it.
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 className="h-5 w-5" /> {t("productSuggestion.productFound", "Product Found")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("productSuggestion.source", "Source:")} <strong data-testid="product-suggestion-source">{providerLabel}</strong>.
                    </DialogDescription>
                </DialogHeader>

                {!editing ? (
                    <IdentificationCard f={f} />
                ) : (
                    <EditFields f={f} upd={upd} />
                )}

                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2 items-start mt-3"
                     data-testid="ps-price-note">
                    <PackageOpen className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        {t("productSuggestion.noPriceImported", "No price is imported from the barcode source. The product price will be set the first time it appears on a confirmed delivery.")}
                    </span>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
                    <button data-testid="ps-cancel"
                        onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-medium">
                        {t("productSuggestion.cancel", "Cancel")}
                    </button>
                    {!editing && (
                        <button data-testid="ps-edit"
                            onClick={() => setEditing(true)}
                            className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-medium">
                            {t("productSuggestion.editBeforeCreating", "Edit Before Creating")}
                        </button>
                    )}
                    <button data-testid={editing ? "ps-create" : "ps-use"}
                        onClick={submit}
                        disabled={!canSubmit || busy}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-40">
                        {busy ? t("productSuggestion.creating", "Creating…") : (editing ? t("productSuggestion.createProduct", "Create Product") : t("productSuggestion.useProduct", "Use Product"))}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Read-only identification card ─────────────────────────────
function IdentificationCard({ f }) {
    const { t } = useTranslation();
    const rows = [
        { key: "product_name", label: t("productSuggestion.productName", "Product Name"), value: f.product_name || "—" },
        { key: "brand",        label: t("productSuggestion.brand", "Brand"),        value: f.brand || "—" },
        { key: "package_size", label: t("productSuggestion.package", "Package"),      value: f.package_size || "—" },
        { key: "unit",         label: t("productSuggestion.unit", "Unit"),         value: f.unit || "—" },
        { key: "category",     label: t("productSuggestion.category", "Category"),     value: f.category || "—" },
        { key: "barcode",      label: t("productSuggestion.barcode", "Barcode"),      value: f.barcode || "—", mono: true },
    ];
    return (
        <dl className="mt-2 space-y-1.5 rounded-md border border-neutral-200 bg-white p-4"
             data-testid="ps-identification-card">
            {rows.map((r) => (
                <div key={r.key}
                     data-testid={`ps-id-${r.key}`}
                     className="flex items-baseline gap-3 border-b border-neutral-100 last:border-0 pb-1.5">
                    <dt className="text-[11px] uppercase tracking-wide text-neutral-500 min-w-[95px]">
                        {r.label}
                    </dt>
                    <dd className={`text-sm font-semibold text-neutral-900 flex-1 text-right ${r.mono ? "font-mono" : ""}`}>
                        {r.value}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

// ─── Edit fields (shown after "Edit Before Creating") ──────────
function EditFields({ f, upd }) {
    const { t } = useTranslation();
    return (
        <div className="grid gap-3 mt-2" data-testid="ps-edit-fields">
            <Field label={t("productSuggestion.productName", "Product Name")} required testId="ps-name-field">
                <input data-testid="ps-name" value={f.product_name}
                    onChange={(e) => upd({ product_name: e.target.value })}
                    className={input} />
            </Field>
            <div className="grid gap-3 grid-cols-2">
                <Field label={t("productSuggestion.brand", "Brand")} testId="ps-brand-field">
                    <input data-testid="ps-brand" value={f.brand}
                        onChange={(e) => upd({ brand: e.target.value })}
                        className={input} />
                </Field>
                <Field label={t("productSuggestion.package", "Package")} testId="ps-package-field">
                    <input data-testid="ps-package" value={f.package_size}
                        onChange={(e) => upd({ package_size: e.target.value })}
                        className={input} />
                </Field>
                <Field label={t("productSuggestion.unit", "Unit")} required testId="ps-unit-field">
                    <select data-testid="ps-unit" value={f.unit}
                        onChange={(e) => upd({ unit: e.target.value })}
                        className={input}>
                        {["kpl", "kg", "g", "l", "ml", "pkt", "ltk"].map((u) => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </Field>
                <Field label={t("productSuggestion.category", "Category")} testId="ps-category-field">
                    <input data-testid="ps-category" value={f.category}
                        onChange={(e) => upd({ category: e.target.value })}
                        className={input} />
                </Field>
            </div>
            <Field label={t("productSuggestion.barcode", "Barcode")} testId="ps-barcode-field">
                <input data-testid="ps-barcode" value={f.barcode} readOnly
                    className={inputRO} />
            </Field>
        </div>
    );
}

const input   = "h-10 w-full px-3 rounded-md border border-neutral-300 text-sm focus:border-neutral-900 focus:outline-none";
const inputRO = "h-10 w-full px-3 rounded-md border border-neutral-200 bg-neutral-50 text-sm text-neutral-500 font-mono";

function Field({ label, required, children, testId }) {
    return (
        <div data-testid={testId}>
            <label className="text-xs font-semibold text-neutral-600">
                {label} {required && <span className="text-rose-600">*</span>}
            </label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
